import { useEffect, useRef, useCallback } from "react";
import { useCalibration } from "../../contexts/CalibrationContext";
import { useShedConfig } from "../../contexts/ShedConfigContext";
import Section from "../layout/Section";

export default function ShedVisualization() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const {
    isSurveyLoaded,
    surveyType,
    surveyData,
    scaleFactor,
    shedPosition,
    shedRotation,
    isShedLocked,
    imageViewerRef,
    pdfViewerRef,
  } = useCalibration();

  const { dimensions, configApplied } = useShedConfig();

  // Default dimensions for initial canvas render; actual dimensions are set in the effect
  const defaultDims = { width: 1200, height: 900 };

  const getSurveyDimensions = useCallback(() => {
    if (surveyType === "image" && imageViewerRef.current) {
      const img = imageViewerRef.current;
      if (img.complete && img.naturalWidth > 0) {
        return {
          width: img.naturalWidth,
          height: img.naturalHeight,
        };
      }
    } else if (surveyType === "pdf" && pdfViewerRef.current) {
      const canvas = pdfViewerRef.current;
      if (canvas.width > 0) {
        return {
          width: canvas.width,
          height: canvas.height,
        };
      }
    }
    return { width: 1200, height: 900 };
  }, [surveyType, imageViewerRef, pdfViewerRef]);

  const sectionTooltip = (
    <>
      This visualization includes:
      <ul>
        <li>W: Total width of structure (Width)</li>
        <li>L: Length or depth (Length)</li>
        <li>H: Total height (Height)</li>
        <li>D: Distance between structure and fence (Setback)</li>
        <li>+: Indicates location of main door</li>
      </ul>
    </>
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dims = getSurveyDimensions();
    canvas.width = dims.width;
    canvas.height = dims.height;

    ctx.fillStyle = "#f3f4f6";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (!isSurveyLoaded || !scaleFactor || !isShedLocked || !configApplied) {
      ctx.fillStyle = "#6b7280";
      ctx.font = "14px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(
        "Complete calibration and lock shed position",
        canvas.width / 2,
        canvas.height / 2 - 10,
      );
      ctx.fillText(
        "to see visualization",
        canvas.width / 2,
        canvas.height / 2 + 10,
      );
      return;
    }

    let sourceElement: HTMLImageElement | HTMLCanvasElement | null = null;

    if (surveyType === "image" && imageViewerRef.current) {
      sourceElement = imageViewerRef.current;
    } else if (surveyType === "pdf" && pdfViewerRef.current) {
      sourceElement = pdfViewerRef.current;
    }

    if (!sourceElement) return;

    ctx.drawImage(sourceElement, 0, 0, canvas.width, canvas.height);

    // Get display dimensions for coordinate conversion
    // scaleFactor is based on display coordinates, but we draw to natural image coordinates
    const displayWidth = sourceElement.offsetWidth || dims.width;
    const displayHeight = sourceElement.offsetHeight || dims.height;

    // Scale factor to convert from display coordinates to natural coordinates
    const displayToNaturalX = dims.width / displayWidth;
    const displayToNaturalY = dims.height / displayHeight;

    // Calculate shed dimensions
    // scaleFactor gives feet-per-display-pixel, so we need to convert to natural pixels
    const calculateShedDimensions = () => {
      // Base dimensions in display pixels
      const baseWidthDisplayPx = (Number(dimensions.width) || 10) / scaleFactor;
      const baseLengthDisplayPx =
        (Number(dimensions.length) || 12) / scaleFactor;

      // Convert to natural image pixels
      const baseWidthPx = baseWidthDisplayPx * displayToNaturalX;
      const baseLengthPx = baseLengthDisplayPx * displayToNaturalY;

      return {
        width: baseWidthPx,
        length: baseLengthPx,
      };
    };

    const { width: shedWidthPx, length: shedLengthPx } =
      calculateShedDimensions();
    const shedX = shedPosition.x;
    const shedY = shedPosition.y;

    ctx.save();
    ctx.translate(shedX, shedY);
    ctx.rotate((shedRotation * Math.PI) / 180);

    const textScale = Math.max(
      Math.min(canvas.width / 800, canvas.height / 600),
      0.8,
    );
    const lineWidth = Math.max(3 * textScale, 2);

    ctx.strokeStyle = "black";
    ctx.lineWidth = lineWidth;
    ctx.setLineDash([]);
    ctx.strokeRect(
      -shedWidthPx / 2,
      -shedLengthPx / 2,
      shedWidthPx,
      shedLengthPx,
    );

    ctx.beginPath();
    ctx.moveTo(-shedWidthPx / 2, -shedLengthPx / 2);
    ctx.lineTo(shedWidthPx / 2, shedLengthPx / 2);
    ctx.moveTo(shedWidthPx / 2, -shedLengthPx / 2);
    ctx.lineTo(-shedWidthPx / 2, shedLengthPx / 2);
    ctx.stroke();

    const fontSize = Math.max(14 * textScale, 12);
    ctx.font = `bold ${fontSize}px Arial`;
    ctx.fillStyle = "black";
    ctx.strokeStyle = "white";
    ctx.lineWidth = Math.max(3 * textScale, 2);
    const textOffset = 20 * textScale;

    const widthText = `W:${dimensions.width || "10"}'`;
    const widthTextWidth = ctx.measureText(widthText).width;
    ctx.strokeText(
      widthText,
      -widthTextWidth / 2,
      -shedLengthPx / 2 - textOffset,
    );
    ctx.fillText(
      widthText,
      -widthTextWidth / 2,
      -shedLengthPx / 2 - textOffset,
    );

    const lengthText = `L:${dimensions.length || "12"}'`;
    ctx.strokeText(lengthText, shedWidthPx / 2 + textOffset, fontSize / 3);
    ctx.fillText(lengthText, shedWidthPx / 2 + textOffset, fontSize / 3);

    const heightText = `H:${dimensions.totalHeight}'`;
    const heightTextWidth = ctx.measureText(heightText).width;
    ctx.strokeText(
      heightText,
      -heightTextWidth / 2,
      shedLengthPx / 2 + textOffset + fontSize,
    );
    ctx.fillText(
      heightText,
      -heightTextWidth / 2,
      shedLengthPx / 2 + textOffset + fontSize,
    );

    if (dimensions.fenceDistance) {
      const distanceText = `D:${dimensions.fenceDistance}'`;
      const distanceTextWidth = ctx.measureText(distanceText).width;
      ctx.strokeText(
        distanceText,
        -shedWidthPx / 2 - textOffset - distanceTextWidth,
        fontSize / 3,
      );
      ctx.fillText(
        distanceText,
        -shedWidthPx / 2 - textOffset - distanceTextWidth,
        fontSize / 3,
      );
    }

    ctx.restore();
  }, [
    isSurveyLoaded,
    surveyType,
    surveyData,
    scaleFactor,
    shedPosition,
    shedRotation,
    isShedLocked,
    configApplied,
    dimensions,
    imageViewerRef,
    pdfViewerRef,
    getSurveyDimensions,
  ]);

  return (
    <Section title="Shed Visualization" tooltip={sectionTooltip}>
      <p className="text-sm mx-4 md:mx-36 mb-2 mt-2">
        Here the structure (shed, carport or garage) is shown projected directly
        on the terrain plan with all entered data. Use this visualization to
        make sure the design is well located, with correct orientation and
        dimensions appropriate to the space. If you need to make changes, you
        can return to previous steps before generating the PDF.
      </p>
      <canvas
        ref={canvasRef}
        width={defaultDims.width}
        height={defaultDims.height}
        className="mx-auto max-w-full h-auto shadow-lg"
      />
    </Section>
  );
}
